import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, FileText, MessageCircle, ReceiptText, Search, ShieldCheck, Users } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useLanguage } from '../contexts/LanguageContext';

const painKeys = ['painOldInvoices', 'painTaxSplit', 'painFollowup', 'painRecords'];
const helpItems = [
  { key: 'helpFastPdf', icon: FileText },
  { key: 'helpAutoTax', icon: ReceiptText },
  { key: 'helpShare', icon: MessageCircle },
  { key: 'helpHistory', icon: Search },
  { key: 'helpCustomersItems', icon: Users },
  { key: 'helpSeal', icon: ShieldCheck },
];
const audienceKeys = ['builtFreelancers', 'builtShops', 'builtInstagram', 'builtServices', 'builtD2c'];

export default function Home() {
  const { t } = useLanguage();

  return (
    <div className="bg-white" data-testid="home-page">
      <section className="relative overflow-hidden border-b border-slate-200 bg-slate-950 text-white">
        <div className="absolute inset-0 opacity-20" style={{ backgroundImage: 'radial-gradient(circle at 20% 20%, #f97316 0, transparent 28%), radial-gradient(circle at 80% 0%, #64748b 0, transparent 24%)' }} />
        <div className="relative mx-auto grid min-h-[640px] max-w-[1180px] grid-cols-1 gap-10 px-4 py-16 sm:px-6 lg:grid-cols-[1.05fr_0.95fr] lg:items-center lg:px-8 lg:py-20">
          <div>
            <div className="mb-4 inline-flex rounded-full border border-orange-400/30 bg-orange-500/10 px-3 py-1 text-[11px] font-black uppercase tracking-[0.16em] text-orange-200">
              {t('homeHeroKicker')}
            </div>
            <h1 className="font-heading text-4xl font-black leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl">
              {t('homeHeroTitle')}
            </h1>
            <p className="mt-5 max-w-xl text-base leading-7 text-slate-300 sm:text-lg">
              {t('homeHeroCopy')}
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <Link to="/create">
                <Button className="h-12 w-full bg-orange-500 px-6 text-base font-black text-white hover:bg-orange-600 sm:w-auto" data-testid="home-create-cta">
                  {t('createInvoice')} <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <Link to="/plan">
                <Button variant="outline" className="h-12 w-full border-white/20 bg-white/5 px-6 text-base font-black text-white hover:bg-white/10 sm:w-auto">
                  {t('seePlan')}
                </Button>
              </Link>
            </div>
            <p className="mt-4 text-sm font-semibold text-slate-400">{t('homePlanCopy')}</p>
          </div>

          <div className="rounded-2xl border border-white/10 bg-white/[0.06] p-4 shadow-2xl backdrop-blur">
            <div className="rounded-xl bg-white p-5 text-slate-900">
              <div className="mb-5 flex items-center justify-between border-b border-slate-200 pb-4">
                <div>
                  <div className="text-[10px] font-black uppercase tracking-[0.18em] text-orange-600">BillJaldi</div>
                  <div className="font-heading text-2xl font-black">{t('invoiceHash')} INV-001</div>
                </div>
                <div className="rounded-lg bg-slate-950 px-3 py-2 text-xs font-black text-white">GST</div>
              </div>
              <div className="space-y-3">
                {helpItems.slice(0, 4).map((item) => (
                  <div key={item.key} className="flex items-center gap-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
                    <item.icon className="h-5 w-5 shrink-0 text-orange-500" />
                    <span className="text-sm font-bold text-slate-700">{t(item.key)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-[1180px] px-4 py-12 sm:px-6 lg:px-8">
        <div className="grid gap-8 lg:grid-cols-[0.85fr_1.15fr] lg:items-start">
          <div>
            <h2 className="font-heading text-3xl font-black tracking-tight text-slate-900">{t('painTitle')}</h2>
            <p className="mt-3 text-sm leading-6 text-slate-600">{t('painCopy')}</p>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            {painKeys.map((key) => (
              <div key={key} className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                <CheckCircle2 className="mb-3 h-5 w-5 text-orange-500" />
                <div className="text-sm font-bold leading-6 text-slate-800">{t(key)}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-y border-slate-200 bg-slate-50">
        <div className="mx-auto max-w-[1180px] px-4 py-12 sm:px-6 lg:px-8">
          <h2 className="font-heading text-3xl font-black tracking-tight text-slate-900">{t('howHelps')}</h2>
          <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {helpItems.map((item) => (
              <div key={item.key} className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
                <item.icon className="mb-4 h-6 w-6 text-orange-500" />
                <div className="text-sm font-black leading-6 text-slate-900">{t(item.key)}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-[1180px] px-4 py-12 sm:px-6 lg:px-8">
        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <div className="text-[10px] font-black uppercase tracking-[0.18em] text-orange-600">{t('builtFor')}</div>
              <div className="mt-3 flex flex-wrap gap-2">
                {audienceKeys.map((key) => (
                  <span key={key} className="rounded-full bg-slate-100 px-3 py-1.5 text-xs font-black text-slate-700">{t(key)}</span>
                ))}
              </div>
            </div>
            <Link to="/create">
              <Button className="h-11 bg-slate-900 px-5 font-black text-white hover:bg-slate-800">
                {t('startFree')}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <footer className="border-t border-slate-200 bg-slate-50">
        <div className="mx-auto flex max-w-[1180px] flex-col gap-3 px-4 py-6 text-xs font-bold text-slate-500 sm:flex-row sm:items-center sm:justify-between sm:px-6 lg:px-8">
          <span>Bill Jaldi is operated by Carpentry Works. © 2026 Carpentry Works. All rights reserved.</span>
          <div className="flex gap-4">
            <Link to="/legal" className="hover:text-orange-600">{t('legal')}</Link>
            <Link to="/privacy" className="hover:text-orange-600">{t('privacy')}</Link>
            <Link to="/changelog" className="hover:text-orange-600">{t('changelog')}</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
